import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, VIEW, EDIT, PUBLISHED, UNPUBLISHED,
} from "const"
import { QidCompany, QidCompanyCreate } from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import { qidCompaniesRoute } from "client/parts/routes"
import {
    StoreType,
    LangType,
    QidCompanyValidation,
    SaveQidCompany,
    closeModal,
    setQidCompanyEdit,
    setQidCompanyReloadList,
    setQidCompanyReloadView,
    setQidCompanyReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    FileLoader,
    SelectDropdown,
    SelectDropdownItem,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditCompanyProps {
    company: QidCompany
}

let isMounted: boolean

const Form = ({
    company,
}: EditCompanyProps) => {
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(company.name)
    const [logoId, setLogoId] = useState(company.logoId)
    const [logo, setLogo] = useState(company.logo)
    const [status, setStatus] = useState(company.status)

    const [validation, setValidation] = useState<QidCompanyValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = company.id
            ? `${qidCompaniesRoute}/${VIEW}/${company.id}`
            : qidCompaniesRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newCompanyData: QidCompanyCreate = {
            name,
            logoId,
            status,
        }

        SaveQidCompany(
            company.id,
            newCompanyData,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newCompanyId) replaceHistoryState(`${qidCompaniesRoute}/${EDIT}/${res.newCompanyId}`)
            }
            if (res.validation) return

            dispatch(setQidCompanyEdit({
                id: res.newCompanyId || company.id,
                logo,
                ...newCompanyData,
            }))

            dispatch(setQidCompanyReloadList(false))
            dispatch(setQidCompanyReloadView(false))
            setTimeout(() => {
                dispatch(setQidCompanyReloadList(true))
                dispatch(setQidCompanyReloadView(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setQidCompanyReloadEdit(false))
                setTimeout(() => {
                    dispatch(setQidCompanyReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.status}</Label>
                        <SelectDropdown title={lang[status as keyof LangType]}>
                            <SelectDropdownItem onClick={() => setStatus(PUBLISHED)}>{lang.published}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setStatus(UNPUBLISHED)}>{lang.unpublished}</SelectDropdownItem>
                        </SelectDropdown>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.image}</Label>
                        <FileLoader
                            value={logo}
                            callback={(file) => {
                                setLogoId(file.id)
                                setLogo(file)
                            }}
                        />
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
